/**
 * Traffic avoidance, in two halves sharing this module's maths:
 *
 *  1. The "Avoid traffic" chip on the route preview - picks, from the options
 *     already fetched, the one that dodges the most congestion while staying
 *     reasonable to drive (not a maze of turns, not absurdly longer).
 *  2. The watcher during navigation - every few minutes it re-asks the router
 *     from the live position, finds which fresh route is the one being driven
 *     (by geometric overlap - the router doesn't label it), and when real
 *     congestion sits ahead AND an easier route saves real time, offers the
 *     switch in one tap. It never reroutes on its own: surprise reroutes in
 *     traffic are how drivers end up in a soi arguing with their phone.
 *
 * "Traffic" is measured two ways because Mapbox reports it two ways: the
 * delay (live duration vs typical) and the congestion annotation (per-segment
 * heavy/severe). Either alone misses cases - a long slow crawl can show a big
 * delay with no severe segment, and a short hard block can show severe
 * segments with modest delay.
 */

import type { LngLat, Route } from '../core/types';
import { distance } from '../core/geo';
import { paceDuration } from './paceModel';
import type { RouteStats } from './routeStats';

/** Live delay vs typical, seconds; 0 when the router sent no traffic data. */
export function trafficDelay(r: Route): number {
  return r.duration_typical != null ? Math.max(0, r.duration - r.duration_typical) : 0;
}

/** Fraction of the route's distance in heavy or severe congestion. */
export function congestedShare(r: Route): number {
  const cong = r.legs[0]?.annotation?.congestion;
  const coords = r.geometry.coordinates as LngLat[];
  if (!cong || cong.length < coords.length - 1) return 0;
  let bad = 0;
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const d = distance(coords[i], coords[i + 1]);
    total += d;
    if (cong[i] === 'heavy' || cong[i] === 'severe') bad += d;
  }
  return total > 0 ? bad / total : 0;
}

/**
 * The option that avoids the most traffic while staying easy to drive.
 *
 * Candidates more than 35% slower than the quickest are out - "avoids
 * traffic" must not mean "an hour longer". Among the rest, congestion counts
 * double (it is what the button is FOR), and turns and signals stand in for
 * how demanding the drive is. Returns null with fewer than two options.
 */
export function pickLowTraffic(
  routes: Route[],
  stats: Array<RouteStats | undefined>,
): number | null {
  if (routes.length < 2) return null;
  const quickest = Math.min(...routes.map(paceDuration));
  let best = -1;
  let bestScore = Infinity;
  routes.forEach((r, i) => {
    if (paceDuration(r) > quickest * 1.35) return;
    const st = stats[i];
    const score =
      trafficDelay(r) * 2 +
      congestedShare(r) * 1800 +
      (st?.turns ?? 0) * 25 +
      (st?.trafficSignals ?? 0) * 40;
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  });
  return best >= 0 ? best : null;
}

/** ~25 evenly spread points of the route still ahead of `alongNow`. */
export function sampleRemaining(coords: LngLat[], cum: number[], alongNow: number): LngLat[] {
  let start = cum.findIndex((d) => d >= alongNow + 500);
  if (start < 0) start = Math.max(0, coords.length - 2);
  const rest = coords.slice(start);
  const step = Math.max(1, Math.floor(rest.length / 25));
  const out: LngLat[] = [];
  for (let i = 0; i < rest.length; i += step) out.push(rest[i]);
  return out;
}

/**
 * How much of `samples` lies on route `r` (within 500 m). Used to recognise
 * "the route we are already on" among a fresh batch of alternatives.
 */
export function overlapShare(samples: LngLat[], r: Route): number {
  if (!samples.length) return 0;
  const coords = r.geometry.coordinates as LngLat[];
  const step = Math.max(1, Math.floor(coords.length / 400));
  let hit = 0;
  for (const p of samples) {
    for (let i = 0; i < coords.length; i += step) {
      if (distance(p, coords[i]) < 500) {
        hit++;
        break;
      }
    }
  }
  return hit / samples.length;
}

export interface TrafficAdvice {
  /** The route being driven, as freshly re-fetched (live traffic applied). */
  stay: Route;
  /** The easier, faster alternative worth offering. */
  alternative: Route;
  /** Seconds the alternative saves over staying. */
  savesSeconds: number;
}

/**
 * Decide whether fresh alternatives justify interrupting the driver.
 *
 * Rules, in order: we must be able to recognise the current route among the
 * fresh ones (≥60% overlap - otherwise we would compare against a stranger);
 * there must be REAL congestion ahead on it (≥5 min delay or ≥12% of the
 * distance heavy/severe); the alternative must be fairly easy (no more than
 * 10 extra turns); and it must save at least 4 minutes. Anything less is not
 * worth a popup while driving.
 */
export function adviseOnTraffic(
  fresh: Route[],
  currentSamples: LngLat[],
  currentStats: (r: Route) => RouteStats,
): TrafficAdvice | null {
  if (fresh.length < 2) return null;

  let stay: Route | null = null;
  let stayOverlap = 0;
  for (const r of fresh) {
    const ov = overlapShare(currentSamples, r);
    if (ov > stayOverlap) {
      stayOverlap = ov;
      stay = r;
    }
  }
  if (!stay || stayOverlap < 0.6) return null;

  if (trafficDelay(stay) < 300 && congestedShare(stay) < 0.12) return null;

  const stayStats = currentStats(stay);
  let best: Route | null = null;
  let bestSave = 0;
  for (const r of fresh) {
    if (r === stay) continue;
    const save = paceDuration(stay) - paceDuration(r);
    if (save <= bestSave) continue;
    if (currentStats(r).turns > stayStats.turns + 10) continue; // a maze is no rescue
    best = r;
    bestSave = save;
  }
  if (!best || bestSave < 240) return null;

  return { stay, alternative: best, savesSeconds: bestSave };
}
